import React, { useState } from "react";
import {
  AlertCircle,
  Calendar,
  User,
  Package,
  Archive,
  Clock,
  Phone,
  Mail,
  Wrench,
  Edit
} from "lucide-react";
import { ComponentProps } from "../types/types";
import { supabaseService } from "../SupabaseService";

const Reklamasjon: React.FC<ComponentProps> = ({
  cases,
  setCases,
  customers,
  setCustomers,
  openCaseForEditing,
  showSuccess,
  showError,
  showWarning,
  showInfo
}) => {
  const [isMoving, setIsMoving] = useState<number | null>(null);

  const reklamasjonCases = cases.filter(
    (case_) => case_.kategori === "Reklamasjon"
  );

  const handleMoveToArchive = async (caseId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Hindre at kort-klikk trigger redigering
    
    const confirmArchive = window.confirm(
      "Er du sikker på at du vil arkivere denne reklamasjonen som ferdig behandlet?"
    );
    
    if (!confirmArchive) return;
    
    setIsMoving(caseId);
    try {
      const caseToMove = cases.find(c => c.id === caseId);
      if (!caseToMove) return;

      // Sett reklamasjon flag til true og flytt til arkiv
      const updatedCase = {
        ...caseToMove,
        kategori: "Arkiv",
        reklamasjon: true,
        arkivertDato: new Date().toISOString(),
        arkivertAv: localStorage.getItem('hagia-user-name') || 'Ukjent'
      };

      // Lagre til Supabase
      const saved = await supabaseService.saveCase(updatedCase);
      
      if (saved) {
        // Oppdater lokal state
        setCases((prev) =>
          prev.map((case_) =>
            case_.id === caseId ? updatedCase : case_
          )
        );
        
        if (showSuccess) {
          showSuccess(
            `Reklamasjon for ${caseToMove.kunde} er arkivert`,
            "Arkivert"
          );
        }
      } else {
        if (showError) {
          showError("Kunne ikke arkivere reklamasjonen", "Feil");
        }
      }
    } catch (error) {
      console.error("Feil ved arkivering:", error);
      if (showError) {
        showError("Teknisk feil ved arkivering", "Systemfeil");
      }
    } finally {
      setIsMoving(null);
    }
  };

  const handleMoveToService = async (caseId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Hindre at kort-klikk trigger redigering
    
    const confirmMove = window.confirm(
      "Vil du flytte denne ordren tilbake til Service?"
    );
    
    if (!confirmMove) return;
    
    setIsMoving(caseId);
    try {
      const caseToMove = cases.find(c => c.id === caseId);
      if (!caseToMove) return;

      const updatedCase = {
        ...caseToMove,
        kategori: "Service",
        kommentar: `${caseToMove.kommentar || ''}\n[Flyttet tilbake fra Reklamasjon ${new Date().toLocaleDateString('nb-NO')}]`.trim()
      };

      const saved = await supabaseService.saveCase(updatedCase);
      
      if (saved) {
        setCases((prev) =>
          prev.map((case_) =>
            case_.id === caseId ? updatedCase : case_
          )
        );
        
        if (showInfo) {
          showInfo(
            `Ordre flyttet tilbake til Service`,
            "Flyttet"
          );
        }
      }
    } catch (error) {
      console.error("Feil ved flytting:", error);
      if (showError) {
        showError("Kunne ikke flytte ordre", "Feil");
      }
    } finally {
      setIsMoving(null);
    }
  };

  const getUrgencyInfo = (caseDate: string) => {
    const now = new Date();
    const created = new Date(caseDate);
    const daysDiff = Math.floor(
      (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysDiff > 14)
      return {
        level: "critical",
        text: `${daysDiff} dager`,
        color: "bg-red-100 text-red-800 border-red-300",
        borderColor: "border-red-400",
        bgGradient: "bg-gradient-to-br from-red-50 to-red-100"
      };
    if (daysDiff > 7)
      return {
        level: "high",
        text: `${daysDiff} dager`,
        color: "bg-orange-100 text-orange-800 border-orange-300",
        borderColor: "border-orange-400",
        bgGradient: "bg-gradient-to-br from-orange-50 to-orange-100"
      };
    if (daysDiff > 3)
      return {
        level: "medium",
        text: `${daysDiff} dager`,
        color: "bg-yellow-100 text-yellow-800 border-yellow-300",
        borderColor: "border-yellow-400",
        bgGradient: "bg-gradient-to-br from-yellow-50 to-yellow-100"
      };
    return {
      level: "normal",
      text: `${daysDiff} dager`,
      color: "bg-green-100 text-green-800 border-green-300",
      borderColor: "border-green-400",
      bgGradient: "bg-gradient-to-br from-green-50 to-green-100"
    };
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="p-4 border-b bg-gradient-to-r from-red-50 to-orange-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AlertCircle className="text-red-600" size={24} />
            <h2 className="text-xl font-semibold text-gray-900">Reklamasjon</h2>
            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
              {reklamasjonCases.length} {reklamasjonCases.length === 1 ? 'sak' : 'saker'}
            </span>
          </div>
          {reklamasjonCases.length > 0 && (
            <div className="text-sm text-red-700 font-medium">
              ⚠️ Krever oppfølging med leverandør
            </div>
          )}
        </div>
      </div>

      <div className="p-4">
        {reklamasjonCases.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="mx-auto text-gray-400 mb-3" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Ingen reklamasjoner
            </h3>
            <p className="text-gray-600">
              Reklamasjonssaker vil vises her når de registreres.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Flytt ordre hit fra andre kategorier når det oppstår reklamasjon.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reklamasjonCases.map((case_) => {
              const urgency = getUrgencyInfo(case_.opprettet || case_.dato);
              
              return (
                <div
                  key={case_.id}
                  onClick={() => openCaseForEditing(case_)}
                  className={`
                    relative p-4 rounded-lg border-2 cursor-pointer
                    transition-all hover:shadow-lg hover:-translate-y-1
                    ${urgency.bgGradient} ${urgency.borderColor}
                  `}
                >
                  {/* Header med ordre-nummer og dager */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-bold text-gray-900">
                      #{case_.id}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${urgency.color}`}>
                      {urgency.text}
                    </span>
                  </div>

                  {/* Kundeinfo */}
                  <div className="flex items-center space-x-2 mb-2">
                    <User className="text-gray-500" size={16} />
                    <h3 className="font-semibold text-gray-900 truncate">
                      {case_.kunde}
                    </h3>
                  </div>

                  {/* Maskininfo */}
                  <div className="space-y-1 mb-3">
                    <div className="flex items-center space-x-2 text-sm text-gray-700">
                      <Package size={14} />
                      <span className="font-medium">{case_.maskinMerke || "Ukjent maskin"}</span>
                    </div>
                    
                    {case_.serienr && (
                      <div className="text-xs text-gray-600 pl-5">
                        SN: {case_.serienr}
                      </div>
                    )}
                  </div>

                  {/* Beskrivelse */}
                  <div className="bg-white bg-opacity-70 rounded p-2 mb-3">
                    <p className="text-sm text-gray-800 line-clamp-2">
                      {case_.beskrivelse || "Ingen beskrivelse"}
                    </p>
                  </div>

                  {/* Kontaktinfo og dato */}
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    {case_.tlf ? (
                      <div className="flex items-center space-x-1">
                        <Phone size={12} />
                        <span>{case_.tlf}</span>
                      </div>
                    ) : (
                      <span></span>
                    )}
                    <div className="flex items-center space-x-1">
                      <Calendar size={12} />
                      <span>{new Date(case_.opprettet || case_.dato).toLocaleDateString("nb-NO")}</span>
                    </div>
                  </div>

                  {/* Handlingsknapper */}
                  <div className="flex space-x-2 mt-3 pt-3 border-t border-gray-200">
                    <button
                      onClick={(e) => handleMoveToService(case_.id, e)}
                      disabled={isMoving === case_.id}
                      className="flex-1 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 
                               disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Flytt tilbake til Service"
                    >
                      {isMoving === case_.id ? (
                        <span className="animate-pulse">...</span>
                      ) : (
                        <span className="flex items-center justify-center space-x-1">
                          <Wrench size={12} />
                          <span>Service</span>
                        </span>
                      )}
                    </button>
                    
                    <button
                      onClick={(e) => handleMoveToArchive(case_.id, e)}
                      disabled={isMoving === case_.id}
                      className="flex-1 px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 
                               disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Arkiver som ferdig behandlet"
                    >
                      {isMoving === case_.id ? (
                        <span className="animate-pulse">...</span>
                      ) : (
                        <span className="flex items-center justify-center space-x-1">
                          <Archive size={12} />
                          <span>Arkiver</span>
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Hover effekt - Rediger indikator */}
                  <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Edit className="text-gray-600" size={16} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reklamasjon;